import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbPencil, TbSearch, TbTrash, TbUserPlus } from "react-icons/tb";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useToast } from "../../../../../Hooks/toast";
import {
  carteiraUsersErrorMessage,
  createBaasAccountUser,
  fetchBaasAccountUsers,
  lookupBaasAccountPersonByDocument,
  revokeBaasAccountUser,
  saveBaasAccountUser,
} from "../../../../../Services/baasAccountUsersApi";
import { isBaasExitRailVirtualAccount } from "../../../../../Services/baasVirtualShells";
import { resolveBaasInstanceCard } from "../../baasPeerOpsPin";
import { CarteiraUserCreateModal } from "../../../Users/CarteiraUserCreateModal";

const roleLabel = {
  admin: "Administrador",
  operator: "Operador",
  viewer: "Visualizador",
};

const statusStyles = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  pending: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
  suspended: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
};

const t = (key, fallback) => {
  const value = intl.get(key);
  return value && value !== key ? value : fallback;
};

const formatLastAccess = (value) => {
  if (!value) return t("walletUsers.page.never", "Nunca");
  return new Intl.DateTimeFormat(intl.getInitOptions?.()?.currentLocale || "pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

const canManageUserRow = (user, manageSupported) => {
  if (!manageSupported) return false;
  const walletId = Number(user?.id);
  if (Number.isFinite(walletId) && walletId > 0) return true;
  const personId = Number(user?.personId);
  return Number.isFinite(personId) && personId > 0;
};

export const BaasAccountAccessPanel = ({ accountId, account = null }) => {
  const { selectedInstance, opsInstance, instances } = useBaasInstance();
  const { isReadOnly, isMaster } = useBaasCapabilities();
  const { addToast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [manageSupported, setManageSupported] = useState(false);
  const [removingKey, setRemovingKey] = useState(null);

  const canManageUsers = (manageSupported || isMaster) && !isReadOnly;
  const isExitRailVirtual = isBaasExitRailVirtualAccount(account);

  // Prefer account tenant, then soft peer ops override, then sidebar pin.
  const portalInstance = useMemo(() => {
    const peerCard = resolveBaasInstanceCard(
      instances,
      account?.instanceId ?? account?.baasInstanceId,
      (opsInstance || selectedInstance)?.environment,
    );
    return peerCard || opsInstance || selectedInstance;
  }, [account, instances, opsInstance, selectedInstance]);

  const accountApiHooks = useMemo(
    () => ({
      lookupPersonByDocument: (document) =>
        lookupBaasAccountPersonByDocument(portalInstance, accountId, document),
      createUser: (payload) => createBaasAccountUser(portalInstance, accountId, payload),
      saveUser: (user, payload) => saveBaasAccountUser(portalInstance, accountId, user, payload),
    }),
    [portalInstance, accountId],
  );

  useEffect(() => {
    let cancelled = false;
    if (!portalInstance || !accountId) {
      setUsers([]);
      setManageSupported(false);
      setUsersLoading(false);
      return undefined;
    }

    setUsersLoading(true);
    (async () => {
      try {
        const result = await fetchBaasAccountUsers(portalInstance, accountId);
        if (!cancelled) {
          setUsers(result.items);
          setManageSupported(Boolean(result.manageSupported));
        }
      } catch (error) {
        if (!cancelled) {
          setUsers([]);
          setManageSupported(false);
          addToast({
            type: "error",
            title: carteiraUsersErrorMessage(
              error,
              t(
                "baas.accounts.detail.access.loadFailed",
                "Não foi possível carregar os acessos desta conta.",
              ),
            ),
          });
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [portalInstance, accountId, refreshKey, addToast]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        (roleLabel[user.role] ?? user.role).toLowerCase().includes(query) ||
        String(user.status ?? "")
          .toLowerCase()
          .includes(query),
    );
  }, [searchQuery, users]);

  const statusLabelFor = (status) => {
    if (status === "active") return t("walletUsers.statusActive", "Ativo");
    if (status === "pending") return t("walletUsers.page.statusPending", "Pendente");
    return t("walletUsers.statusSuspended", "Suspenso");
  };

  const handleRemove = useCallback(
    async (user) => {
      if (!canManageUsers || !canManageUserRow(user, manageSupported)) return;
      const key = `${user.personId ?? "p"}-${user.id || "legacy"}`;
      if (removingKey) return;

      const confirmed = window.confirm(
        t(
          "baas.accounts.detail.access.removeConfirm",
          "Remover o acesso desta pessoa à conta?",
        ),
      );
      if (!confirmed) return;

      setRemovingKey(key);
      try {
        await revokeBaasAccountUser(portalInstance, accountId, user);
        addToast({
          type: "success",
          title: t("baas.accounts.detail.access.removeSuccess", "Acesso removido."),
        });
        setRefreshKey((current) => current + 1);
      } catch (error) {
        addToast({
          type: "error",
          title: carteiraUsersErrorMessage(
            error,
            t("baas.accounts.detail.access.removeFailed", "Não foi possível remover o acesso."),
          ),
        });
      } finally {
        setRemovingKey(null);
      }
    },
    [
      accountId,
      addToast,
      canManageUsers,
      manageSupported,
      removingKey,
      portalInstance,
    ],
  );

  if (usersLoading) {
    return (
      <div className="app-card px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
        {t("baas.accounts.detail.access.loading", "Carregando acessos…")}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-brand-dark dark:text-gray-100">
          {t("baas.accounts.detail.access.title", "Acessos")}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t(
            "baas.accounts.detail.access.subtitle",
            "Pessoas com acesso à Carteira desta conta (perfil e permissões).",
          )}
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-md flex-1">
          <TbSearch
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
            aria-hidden
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t(
              "baas.accounts.detail.access.searchPlaceholder",
              "Buscar por nome, e-mail ou perfil",
            )}
            className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
          />
        </div>

        {canManageUsers ? (
          <button
            type="button"
            className="btn-primary shrink-0 !h-10"
            onClick={() => {
              setEditingUser(null);
              setCreateModalOpen(true);
            }}
          >
            <TbUserPlus size={18} aria-hidden />
            {t("baas.accounts.detail.access.addUser", "Adicionar usuário")}
          </button>
        ) : null}
      </div>

      <div className="app-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50/80 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">{t("walletUsers.page.colName", "Nome")}</th>
                <th className="px-4 py-3">{t("walletUsers.page.colEmail", "E-mail")}</th>
                <th className="px-4 py-3">{t("walletUsers.page.colRole", "Perfil")}</th>
                <th className="px-4 py-3">{t("walletUsers.page.colStatus", "Status")}</th>
                <th className="px-4 py-3">{t("walletUsers.page.colLastAccess", "Último acesso")}</th>
                {canManageUsers ? (
                  <th className="px-4 py-3">{t("walletUsers.page.colActions", "Ações")}</th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-brand-dark-border">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={canManageUsers ? 6 : 5}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    {searchQuery.trim()
                      ? t("walletUsers.page.emptySearch", "Nenhum usuário encontrado para a busca.")
                      : t(
                          "baas.accounts.detail.access.empty",
                          "Nenhum usuário vinculado a esta conta.",
                        )}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const rowKey = `${user.personId ?? "p"}-${user.id || "legacy"}-${user.email}`;
                  const canAct = canManageUsers && canManageUserRow(user, manageSupported);
                  const isRemoving = removingKey === `${user.personId ?? "p"}-${user.id || "legacy"}`;
                  return (
                    <tr
                      key={rowKey}
                      className="hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
                    >
                      <td className="px-4 py-3 font-medium text-brand-dark dark:text-gray-100">
                        {user.name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{user.email}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {roleLabel[user.role] ?? user.role}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            statusStyles[user.status] ?? statusStyles.suspended
                          }`}
                        >
                          {statusLabelFor(user.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {formatLastAccess(user.lastAccess)}
                      </td>
                      {canManageUsers ? (
                        <td className="px-4 py-3">
                          {canAct ? (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                className="btn-outline-icon"
                                aria-label={`Editar ${user.name}`}
                                title={t("walletUsers.page.editUser", "Editar permissões")}
                                onClick={() => {
                                  setEditingUser(user);
                                  setCreateModalOpen(true);
                                }}
                              >
                                <TbPencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                className="btn-outline-icon text-red-600 hover:text-red-700 dark:text-red-400"
                                aria-label={`Remover ${user.name}`}
                                title={t("baas.accounts.detail.access.remove", "Remover acesso")}
                                disabled={isRemoving}
                                onClick={() => handleRemove(user)}
                              >
                                <TbTrash className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {canManageUsers ? (
        <CarteiraUserCreateModal
          open={createModalOpen}
          user={editingUser}
          requireTransactionalPassword={false}
          isExitRailVirtualOverride={isExitRailVirtual}
          lookupPersonByDocument={accountApiHooks.lookupPersonByDocument}
          createUser={accountApiHooks.createUser}
          saveUser={accountApiHooks.saveUser}
          onClose={() => {
            setCreateModalOpen(false);
            setEditingUser(null);
          }}
          onSaved={() => setRefreshKey((current) => current + 1)}
        />
      ) : null}
    </div>
  );
};

export default BaasAccountAccessPanel;
